import {
  authorizationFor,
  type AuthWebConfig,
  type CallbackOutcome,
  type Identity,
  installShellAuth,
  type ShellAuth
} from '@thecvlb/auth-web';

import { type LoginChoice, readLoginChoice } from './loginServiceOptIn';

/**
 * The LifeMD login service (`@thecvlb/auth-web`, LMD-682): renders sign-in, issues and renews tokens,
 * and decides legacy vs WorkOS (`workos-patient-auth`). Active only where a login host is known
 * (`loginServiceOrigin`) and the browser has opted in (utils/loginServiceOptIn). No store imports, so the request
 * layer can use it without an import cycle.
 */

/** Must be the first callback of this origin's registration on the login service. */
export const LOGIN_CALLBACK_PATH = '/_auth/callback';

const APP_NAME = 'LifeMD Patient Portal';

let auth: ShellAuth | null = null;

const httpsOrigin = (url: string | undefined) => {
  try {
    const parsed = new URL(url ?? '');
    return parsed.protocol === 'https:' ? parsed.origin : null;
  } catch {
    return null;
  }
};

/**
 * The login host for a portal origin registered on the login service, used until the deployment sets
 * `VITE_LOGIN_SERVICE_ORIGIN`. Only exact origins: preview and other hosts get none.
 */
const DEFAULT_LOGIN_ORIGINS: Record<string, string> = {
  'https://care.dev.lifemd.com': 'https://login.dev.lifemd.com',
  // Hotfix is registered under stage (idp-backend-bootstrap).
  'https://care.hotfix.lifemd.com': 'https://login.stage.lifemd.com',
  'https://care.stage.lifemd.com': 'https://login.stage.lifemd.com',
  'https://localhost:9000': 'https://localhost:4100'
};

/** `VITE_LOGIN_SERVICE_ORIGIN` when set, else the default for this page's origin, else none. */
export const loginServiceOrigin = (
  env: Record<string, string | undefined> = import.meta.env,
  pageOrigin: string = typeof window === 'undefined' ? '' : window.location.origin
): string | undefined => env.VITE_LOGIN_SERVICE_ORIGIN || DEFAULT_LOGIN_ORIGINS[pageOrigin];

export const loginServiceConfig = (
  env: Record<string, string | undefined> = import.meta.env,
  choice: LoginChoice | null = readLoginChoice(),
  pageOrigin?: string
): AuthWebConfig | null => {
  const origin = loginServiceOrigin(env, pageOrigin);

  // A login host makes the login service available; a browser uses it only after opting in
  // (utils/loginServiceOptIn), so everyone else keeps the portal's own sign-in.
  if (!origin || !choice) {
    return null;
  }

  const apiOrigin = httpsOrigin(env.VITE_BASE_URL);
  const unleash =
    env.VITE_UNLEASH_PROXY_URL && env.VITE_UNLEASH_CLIENT_KEY
      ? {
          appName: APP_NAME,
          clientKey: env.VITE_UNLEASH_CLIENT_KEY,
          url: env.VITE_UNLEASH_PROXY_URL
        }
      : undefined;

  return {
    callbackPath: LOGIN_CALLBACK_PATH,
    loginService: { origin },
    trustedOrigins: apiOrigin ? [apiOrigin] : [],
    unleash,
    ...(choice === 'new-workos' ? { modern: true } : {})
  };
};

/** Installs `window.shell.auth`; null without a login host. Throws `ConfigError` if misconfigured. */
export const installLoginService = (config = loginServiceConfig()): ShellAuth | null => {
  auth = config ? installShellAuth(config) : null;
  return auth;
};

export const getLoginService = () => auth;

/**
 * Bare for legacy tokens, `Bearer` otherwise; the session's source decides (the SDK never decodes
 * tokens). Portal-signed sessions have no source and stay bare.
 */
export const authorizationHeader = (token: string, source?: string) =>
  auth && source ? authorizationFor(token, source) : token;

/** Reformats an endpoint's `Authorization` only if it is the session's own token (not sign-up/guest). */
export const formatSessionAuthorization = (
  headers: Headers,
  user: { accessToken?: string; authSource?: string } | undefined
) => {
  const value = headers.get('Authorization');

  if (value && user?.accessToken && value === user.accessToken) {
    headers.set('Authorization', authorizationHeader(value, user.authSource));
  }
};

/**
 * Session key built from who is signed in, not the token (a platform token gets a new `jti` each
 * renewal). Staff acting as a patient key apart from the patient.
 */
export const sessionKeyOfIdentity = (identity: Identity) =>
  [identity.authSource, identity.kind, identity.subject, identity.actor?.subject ?? ''].join(':');

/**
 * Acts on `handleCallback`'s outcome: `redirect` navigates by itself; a handed-off popup/tab still
 * open, or a callback this tab never started, goes to the start page; a failure or throw shows an
 * error with a retry instead of a blank page.
 */
export const finishLoginCallback = (
  callback: () => CallbackOutcome,
  page: { leave: () => void; showError: () => void }
) => {
  let outcome: CallbackOutcome;

  try {
    outcome = callback();
  } catch {
    page.showError();
    return;
  }

  if (outcome === 'failed') {
    page.showError();
  } else if (outcome === 'handed-off' || outcome === 'ignored') {
    page.leave();
  }
};

/** The callback page's error, written without starting the app: the page installs nothing else. */
export const showLoginCallbackError = (container: HTMLElement | null, retryHref = '/') => {
  if (!container) {
    return;
  }

  const title = document.createElement('h1');
  title.textContent = 'We couldn’t sign you in';
  const message = document.createElement('p');
  message.textContent = 'Please try signing in again.';
  const retry = document.createElement('a');
  retry.href = retryHref;
  retry.textContent = 'Try again';

  const alert = document.createElement('main');
  alert.setAttribute('role', 'alert');
  alert.append(title, message, retry);
  container.replaceChildren(alert);
};

/** For tests: forget the installed provider. */
export const resetLoginService = () => {
  auth = null;
};
